/**
 * Read a File as a data URL (data:image/...;base64,...).
 */
export function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if(typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }
            reject(new Error('failed to read file'));
        };
        reader.onerror = () => reject(reader.error || new Error('failed to read file'));
        reader.readAsDataURL(file);
    });
}

const IMAGE_MIME_RE = /^image\/(jpeg|jpg|png|webp|gif)$/i;
const HEIC_MIME_RE = /^image\/hei[cf]$/i;
// The browser reports an empty MIME type for HEIC when the OS has no app registered for it.
const HEIC_EXT_RE = /\.hei[cf]$/i;
const PDF_MIME = 'application/pdf';

export function isAiAgentVisionImage(file: File): boolean {
    return IMAGE_MIME_RE.test(file.type);
}

/** iPhone photos — converted to JPEG in the browser before upload (see convertHeicFile). */
export function isAiAgentHeicImage(file: File): boolean {
    return HEIC_MIME_RE.test(file.type) || (!file.type && HEIC_EXT_RE.test(file.name));
}

export function isAiAgentDocumentFile(file: File): boolean {
    return file.type.toLowerCase() === PDF_MIME;
}

/** Everything the agent can read: vision images (HEIC after conversion) plus PDF documents. */
export function isAiAgentSupportedFile(file: File): boolean {
    return isAiAgentVisionImage(file) || isAiAgentHeicImage(file) || isAiAgentDocumentFile(file);
}

/** Images follow the strictest provider (Anthropic refuses a single image above 5MB raw). */
export const AI_AGENT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** PDFs get more headroom — the server caps their data URL at ~9MB raw. */
export const AI_AGENT_MAX_FILE_BYTES = 8 * 1024 * 1024;

export function exceedsAiAgentSizeCap(file: File): boolean {
    return file.size > (isAiAgentDocumentFile(file) ? AI_AGENT_MAX_FILE_BYTES : AI_AGENT_MAX_IMAGE_BYTES);
}
